import React, { useState, useMemo } from "react";
import { FileText, Monitor, Clock, ChevronDown, Search } from "lucide-react";
import { Loan, LoanStatus } from "../../types/loan.types";
import { COMPANY_COLORS } from "../../constants/theme.constants";
import { StatusChip } from "./StatusChip";
import { getCurrency } from "../../utils/countries";
import { FilterType } from "../common/Header";
import { getFilterLabel } from "../../utils/loanFilters";
import {
  getLinearSteps,
  getActiveStepIndex,
  isRejectionStatus,
  groupParallelSteps,
  getTaskIcon,
  FlowStep,
} from "../../config/flowConfigs";

// ── Types ─────────────────────────────────────────────────────────────────────

interface LoanTableProps {
  loans: Loan[];
  country: string;
  isLoading?: boolean;
  activeFilter?: FilterType;
  totalLoans?: number;
  search: string;
  setSearch: (s: string) => void;
  statusFilter: LoanStatus | "all";
  setStatusFilter: (s: LoanStatus | "all") => void;
  currentPage: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  isAdmin?: boolean;
}

// ── LoanTable ─────────────────────────────────────────────────────────────────

export const LoanTable: React.FC<LoanTableProps> = ({
  loans,
  country,
  isLoading = false,
  activeFilter = "all",
  totalLoans = 0,
  search,
  setSearch,
  statusFilter,
  setStatusFilter,
  currentPage,
  pageSize,
  onPageChange,
  isAdmin = false,
}) => {
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const currency = getCurrency(country);

  const toggleRowExpand = (loanId: string) => {
    const newExpanded = new Set(expandedRows);
    newExpanded.has(loanId) ? newExpanded.delete(loanId) : newExpanded.add(loanId);
    setExpandedRows(newExpanded);
  };

  const getClientInitials = (firstName: string, lastName: string) =>
    `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();

  const formatUpdatedTime = (updatedAt: Date): string => {
    const diff = Math.floor((Date.now() - new Date(updatedAt).getTime()) / 1000);
    if (diff < 60) return "Just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  const avatarStyles = [
    { bg: "#FDECEA", color: "#C62828" },
    { bg: "#E8F5E9", color: "#1B5E20" },
    { bg: "#FFF8E1", color: "#E65100" },
    { bg: "#E8EAF6", color: "#283593" },
    { bg: "#E0F2F1", color: "#004D40" },
    { bg: "#F3E5F5", color: "#4A148C" },
    { bg: "#FCE4EC", color: "#880E4F" },
  ];
  const getAvatarStyle = (i: number) => avatarStyles[i % avatarStyles.length];

  // Build a map of status -> set of systems that use it
  const statusSystemMap = useMemo(() => {
    const map = new Map<string, Set<string>>();
    loans.forEach((loan) => {
      if (!map.has(loan.status)) map.set(loan.status, new Set());
      map.get(loan.status)!.add(loan.system);
    });
    return map;
  }, [loans]);

  // Each option has a display label with system in brackets
  const statusOptions: { value: LoanStatus | "all"; label: string }[] = [
    { value: "all", label: "All Statuses" },
    ...Array.from(statusSystemMap.entries()).map(([status, systems]) => ({
      value: status as LoanStatus,
      label: `${status} (${Array.from(systems).join(", ")})`,
    })),
  ];

  const sortedLoans = [...loans].sort((a, b) => {
    const aT = new Date(a.updatedAt).getTime();
    const bT = new Date(b.updatedAt).getTime();
    return sortOrder === "asc" ? aT - bT : bT - aT;
  });

  // ── Loading ────────────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div
        className="rounded-2xl border overflow-hidden h-screen flex items-center justify-center"
        style={{ background: COMPANY_COLORS.surface, borderColor: COMPANY_COLORS.border }}
      >
        <div className="text-center">
          <div
            className="animate-spin rounded-full h-14 w-14 border-b-2 mx-auto mb-4"
            style={{ borderColor: COMPANY_COLORS.primary }}
          />
          <p className="text-base font-semibold" style={{ color: COMPANY_COLORS.textMuted }}>
            Loading loans…
          </p>
        </div>
      </div>
    );
  }

  // ── Main ──────────────────────────────────────────────────────────────────
  return (
    <div
      className="rounded-2xl border overflow-hidden flex flex-col"
      style={{
        background: COMPANY_COLORS.surface,
        borderColor: COMPANY_COLORS.border,
        height: "calc(100vh - 148px)",
        marginTop: 6,
      }}
    >
      {/* ── Toolbar ── */}
      <div
        className="flex flex-col md:flex-row gap-2 md:gap-3 px-4 py-3 border-b"
        style={{ background: COMPANY_COLORS.surface, borderColor: COMPANY_COLORS.border }}
      >
        <div className="relative" style={{ width: 280 }}>
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2"
            style={{ color: COMPANY_COLORS.textMuted }}
          />
          <input
            type="text"
            placeholder="Search by Loan ID or Client Name"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-lg pl-8 pr-3 py-2 text-xs outline-none transition-colors"
            style={{
              background: COMPANY_COLORS.pageBg,
              border: `1px solid ${COMPANY_COLORS.border}`,
              color: COMPANY_COLORS.textPrimary,
            }}
            onFocus={(e) => (e.target.style.borderColor = COMPANY_COLORS.secondary)}
            onBlur={(e) => (e.target.style.borderColor = COMPANY_COLORS.border)}
          />
        </div>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as LoanStatus | "all")}
          className="rounded-lg px-3 py-2 text-xs outline-none cursor-pointer"
          style={{
            width: 280,
            background: COMPANY_COLORS.pageBg,
            border: `1px solid ${COMPANY_COLORS.border}`,
            color: COMPANY_COLORS.textPrimary,
          }}
        >
          {statusOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>

        <div className="flex items-center gap-2 rounded-lg px-4 py-2 text-xs font-semibold ml-auto">
          <p>Total No of Transactions {loans.length}</p>
        </div>
      </div>

      {/* Active filter banner */}
      {activeFilter !== "all" && (
        <div
          className="px-4 py-2 border-b flex items-center justify-between text-xs font-medium"
          style={{
            background: COMPANY_COLORS.goldPale,
            borderColor: COMPANY_COLORS.goldBorder,
            color: "#8a5a00",
          }}
        >
          <span>Filtered by: {getFilterLabel(activeFilter)}</span>
          <span style={{ color: COMPANY_COLORS.textMuted }}>
            {loans.length} of {totalLoans} loans
          </span>
        </div>
      )}

      {/* ── Table ── */}
      <div className="flex-1 overflow-auto">
        <table className="w-full border-collapse">
          <thead
            className="sticky top-0 z-10"
            style={{ background: COMPANY_COLORS.primary }}
          >
            <tr>
              <th className="w-10 px-4 py-3" />
              {["Loan ID", "Client Name", ...(isAdmin ? ["Country"] : []), "System", "Status"].map((h) => (
                <th
                  key={h}
                  className="px-4 py-3 text-left text-xs font-bold tracking-wider uppercase"
                  style={{ color: COMPANY_COLORS.secondary }}
                >
                  {h}
                </th>
              ))}
              <th
                className="px-4 py-3 text-center text-xs font-bold tracking-wider uppercase cursor-pointer select-none"
                style={{ color: COMPANY_COLORS.secondary }}
                onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
                title={`Sort ${sortOrder === "asc" ? "descending" : "ascending"}`}
              >
                Last Updated&nbsp;{sortOrder === "asc" ? "▲" : "▼"}
              </th>
            </tr>
          </thead>

          <tbody>
            {sortedLoans.length === 0 ? (
              <tr>
                <td
                  colSpan={isAdmin ? 7 : 6}
                  className="px-4 py-16 text-center"
                  style={{ color: COMPANY_COLORS.textMuted }}
                >
                  <FileText size={40} className="mx-auto mb-3" style={{ color: COMPANY_COLORS.border }} />
                  <p className="font-semibold text-sm">No loans found</p>
                  <p className="text-xs mt-1">
                    {activeFilter !== "all"
                      ? `No loans match: ${getFilterLabel(activeFilter)}`
                      : "No loans available for this country"}
                  </p>
                </td>
              </tr>
            ) : (
              sortedLoans.map((loan, index) => {
                const isExpanded = expandedRows.has(loan.id);
                const avatarStyle = getAvatarStyle(index);

                return (
                  <React.Fragment key={loan.id}>
                    <tr
                      className="cursor-pointer transition-colors duration-150 group"
                      style={{
                        background: isExpanded
                          ? "#F8FAFF"
                          : index % 2 === 0
                          ? COMPANY_COLORS.surface
                          : "#FAFBFE",
                        borderBottom: `1px solid ${COMPANY_COLORS.border}`,
                      }}
                      onMouseEnter={(e) =>
                        !isExpanded &&
                        ((e.currentTarget as HTMLElement).style.background = "#F5F7FF")
                      }
                      onMouseLeave={(e) =>
                        !isExpanded &&
                        ((e.currentTarget as HTMLElement).style.background =
                          index % 2 === 0 ? COMPANY_COLORS.surface : "#FAFBFE")
                      }
                      onClick={() => toggleRowExpand(loan.id)}
                    >
                      <td className="px-4 py-3 w-10">
                        <div
                          className="w-6 h-6 rounded-md flex items-center justify-center transition-all duration-200"
                          style={{
                            background: isExpanded ? COMPANY_COLORS.primary : COMPANY_COLORS.pageBg,
                            border: `1px solid ${isExpanded ? COMPANY_COLORS.primary : COMPANY_COLORS.border}`,
                          }}
                        >
                          <ChevronDown
                            size={13}
                            style={{
                              color: isExpanded ? "#fff" : COMPANY_COLORS.primary,
                              transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                              transition: "transform 0.25s",
                            }}
                          />
                        </div>
                      </td>

                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div
                            className="p-1.5 rounded-lg flex-shrink-0"
                            style={{ background: `${COMPANY_COLORS.secondary}18` }}
                          >
                            <FileText size={13} style={{ color: COMPANY_COLORS.secondary }} />
                          </div>
                          <span className="font-semibold text-xs font-mono" style={{ color: COMPANY_COLORS.textPrimary }}>
                            {loan.id}
                          </span>
                        </div>
                      </td>

                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div
                            className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                            style={{ background: avatarStyle.bg, color: avatarStyle.color }}
                          >
                            {getClientInitials(loan.clientFirstName, loan.clientLastName)}
                          </div>
                          <p className="font-semibold text-xs" style={{ color: COMPANY_COLORS.textPrimary }}>
                            {loan.clientFirstName} {loan.clientLastName}
                          </p>
                        </div>
                      </td>

                      {isAdmin && (
                        <td className="px-4 py-3">
                          <span className="text-xs font-semibold" style={{ color: COMPANY_COLORS.textPrimary }}>
                            {loan.country}
                          </span>
                        </td>
                      )}

                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div
                            className="p-1.5 rounded-lg flex-shrink-0"
                            style={{ background: `${COMPANY_COLORS.primary}10` }}
                          >
                            <Monitor size={13} style={{ color: COMPANY_COLORS.primaryLight }} />
                          </div>
                          <span className="text-xs font-medium capitalize" style={{ color: COMPANY_COLORS.textMuted }}>
                            {loan.system}
                          </span>
                        </div>
                      </td>

                      <td className="px-4 py-3 text-center">
                        <div className="flex justify-center">
                          <StatusChip status={loan.status} />
                        </div>
                      </td>

                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-1 text-xs" style={{ color: COMPANY_COLORS.textMuted }}>
                          <Clock size={11} />
                          <span className="font-medium">{formatUpdatedTime(loan.updatedAt)}</span>
                        </div>
                      </td>
                    </tr>

                    {isExpanded && (
                      <tr>
                        <td
                          colSpan={isAdmin ? 7 : 6}
                          className="px-4 py-5"
                          style={{
                            background: "#F8FAFF",
                            borderBottom: `1px solid ${COMPANY_COLORS.border}`,
                          }}
                        >
                          <ExpandedLoanDetails loan={loan} currency={currency} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* ── Pagination Footer ── */}
      <div
        className="px-4 py-3 border-t flex items-center justify-between text-xs"
        style={{ background: COMPANY_COLORS.surface, borderColor: COMPANY_COLORS.border }}
      >
        <div style={{ color: COMPANY_COLORS.textMuted }}>
          Showing {(currentPage - 1) * pageSize + 1} to{" "}
          {Math.min(currentPage * pageSize, totalLoans)} of {totalLoans} loans
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="px-3 py-1.5 rounded-lg border font-semibold transition-all disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-50"
            style={{ borderColor: COMPANY_COLORS.border, color: COMPANY_COLORS.textPrimary }}
          >
            Previous
          </button>

          <div className="flex items-center gap-1">
            {Array.from({ length: Math.ceil(totalLoans / pageSize) }, (_, i) => i + 1)
              .filter((p) => {
                const totalPages = Math.ceil(totalLoans / pageSize);
                return p === 1 || p === totalPages || (p >= currentPage - 1 && p <= currentPage + 1);
              })
              .map((p, i, arr) => {
                const showEllipsis = i > 0 && p !== arr[i - 1] + 1;
                return (
                  <React.Fragment key={p}>
                    {showEllipsis && <span className="px-1 text-gray-400">...</span>}
                    <button
                      onClick={() => onPageChange(p)}
                      className="w-8 h-8 rounded-lg flex items-center justify-center font-bold transition-all"
                      style={{
                        background: currentPage === p ? COMPANY_COLORS.primary : "transparent",
                        color: currentPage === p ? "#fff" : COMPANY_COLORS.textPrimary,
                        border: currentPage === p ? "none" : `1px solid ${COMPANY_COLORS.border}`,
                      }}
                    >
                      {p}
                    </button>
                  </React.Fragment>
                );
              })}
          </div>

          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage >= Math.ceil(totalLoans / pageSize)}
            className="px-3 py-1.5 rounded-lg border font-semibold transition-all disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-50"
            style={{ borderColor: COMPANY_COLORS.border, color: COMPANY_COLORS.textPrimary }}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

// ── TransactionJourney ────────────────────────────────────────────────────────

interface TransactionJourneyProps {
  system: string;
  status: string;
  tasks?: Loan["task_statuses"];
}

const TransactionJourney: React.FC<TransactionJourneyProps> = ({ system, status, tasks }) => {
  const isFleetzen = system.toLowerCase() === "fleetzen";
  const hasTasks = tasks && tasks.length > 0;

  // 1. Determine the steps
  let linearSteps: FlowStep[] = [];
  if (isFleetzen && hasTasks) {
    linearSteps = tasks.map((t) => ({
      key: t.name,
      icon: getTaskIcon(t.name),
    }));
  } else {
    linearSteps = getLinearSteps(system);
  }

  // 2. Group them (Fleetzen dynamic tasks are treated as linear for now)
  const grouped = (isFleetzen && hasTasks) ? linearSteps : groupParallelSteps(linearSteps);

  // 3. Build flat list for indexing
  const flatSteps: FlowStep[] = [];
  grouped.forEach((item) => {
    if (Array.isArray(item)) item.forEach((s) => flatSteps.push(s));
    else flatSteps.push(item);
  });

  // 4. Find active index
  let flatActiveIdx = -1;
  if (isFleetzen && hasTasks) {
    // Current task is the first one that is NOT done
    flatActiveIdx = tasks.findIndex((t) => !t.done);
    if (flatActiveIdx === -1) flatActiveIdx = tasks.length - 1; // All done
  } else {
    flatActiveIdx = flatSteps.findIndex((s) => s.key === status);
  }

  const isRejected = isRejectionStatus(system, status);


  return (
    <div
      className="rounded-xl p-4"
      style={{ background: COMPANY_COLORS.surface, border: `1px solid ${COMPANY_COLORS.border}` }}
    >
      {/* Header */}
      <div className="mb-4 pb-2 flex items-center justify-between" style={{ borderBottom: `2px solid ${COMPANY_COLORS.secondary}` }}>
        <h3 className="text-xs font-bold uppercase tracking-wider" style={{ color: COMPANY_COLORS.primary }}>
          Transaction Journey
        </h3>
        <div className="flex items-center gap-2">
          <span
            className="text-xs px-2 py-0.5 rounded-full font-semibold"
            style={{
              background: `${COMPANY_COLORS.secondary}20`,
              color: COMPANY_COLORS.secondary,
            }}
          >
            {system}
          </span>
          {isRejected && (
            <span
              className="text-xs px-2 py-0.5 rounded-full font-semibold"
              style={{ background: "#FEE2E2", color: "#B91C1C" }}
            >
              Off track — {status}
            </span>
          )}
        </div>
      </div>

      {/* Stepper */}
      <div className="overflow-x-auto pb-2">
        <div className="flex items-start min-w-max relative" style={{ gap: 0 }}>
          {/* Background line */}
          <div
            className="absolute top-3 h-0.5"
            style={{
              left: 0,
              right: 0,
              background: COMPANY_COLORS.border,
              zIndex: 0,
            }}
          />
          {/* Progress fill */}
          {!isRejected && flatActiveIdx >= 0 && (
            <div
              className="absolute top-3 h-0.5 transition-all duration-500"
              style={{
                left: 0,
                width: flatSteps.length > 1
                  ? `${(flatActiveIdx / (flatSteps.length - 1)) * 100}%`
                  : "0%",
                background: COMPANY_COLORS.primary,
                zIndex: 0,
              }}
            />
          )}

          {grouped.map((item, groupIdx) => {
            if (Array.isArray(item)) {
              // ── Parallel group ──────────────────────────────────────────
              return (
                <div
                  key={`group-${groupIdx}`}
                  className="flex flex-col gap-1 relative"
                  style={{ zIndex: 1, minWidth: 100 }}
                >
                  {item.map((step) => {
                    const stepFlatIdx = flatSteps.findIndex((s) => s.key === step.key);
                    const isCompleted = isFleetzen && hasTasks
                      ? tasks[stepFlatIdx]?.done
                      : !isRejected && stepFlatIdx < flatActiveIdx;
                    const isCurrent = isFleetzen && hasTasks
                      ? stepFlatIdx === flatActiveIdx
                      : !isRejected && step.key === status;

                    return (
                      <div key={step.key} className="flex items-center gap-1.5 px-2">
                        {/* Branch line */}
                        <div
                          className="w-3 h-0.5 flex-shrink-0"
                          style={{
                            background: isCompleted || isCurrent
                              ? COMPANY_COLORS.primary
                              : COMPANY_COLORS.border,
                          }}
                        />
                        {/* Dot */}
                        <div
                          className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold border flex-shrink-0"
                          style={{
                            background: isCompleted
                              ? COMPANY_COLORS.primary
                              : isCurrent
                              ? COMPANY_COLORS.secondary
                              : COMPANY_COLORS.surface,
                            borderColor: isCompleted
                              ? COMPANY_COLORS.primary
                              : isCurrent
                              ? COMPANY_COLORS.secondary
                              : COMPANY_COLORS.border,
                            color: isCompleted || isCurrent ? "#fff" : COMPANY_COLORS.textMuted,
                            boxShadow: isCurrent ? `0 0 0 3px ${COMPANY_COLORS.secondary}35` : "none",
                          }}
                        >
                          {isCompleted ? "✓" : isCurrent ? step.icon : ""}
                        </div>
                        {/* Label */}
                        <span
                          style={{
                            fontSize: 9,
                            fontWeight: isCurrent ? 700 : isCompleted ? 600 : 400,
                            color: isCompleted
                              ? COMPANY_COLORS.primary
                              : isCurrent
                              ? COMPANY_COLORS.secondary
                              : COMPANY_COLORS.textMuted,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {step.key}
                          {isCurrent && (
                            <span
                              className="ml-1 px-1 py-0.5 rounded-full text-white"
                              style={{ fontSize: 7, background: COMPANY_COLORS.secondary }}
                            >
                              Current
                            </span>
                          )}
                        </span>
                      </div>
                    );
                  })}
                </div>
              );
            }

            // ── Linear step ───────────────────────────────────────────────
            const step = item as FlowStep;
            const stepFlatIdx = flatSteps.findIndex((s) => s.key === step.key);
            const isCompleted = isFleetzen && hasTasks
              ? tasks[stepFlatIdx]?.done
              : !isRejected && stepFlatIdx < flatActiveIdx;
            const isCurrent = isFleetzen && hasTasks
              ? stepFlatIdx === flatActiveIdx
              : !isRejected && step.key === status;

            return (
              <div
                key={step.key}
                className="flex flex-col items-center relative"
                style={{ zIndex: 1, minWidth: 72, flex: 1 }}
              >
                {/* Dot */}
                <div
                  className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold border-2 transition-all duration-300"
                  style={{
                    background: isCompleted
                      ? COMPANY_COLORS.primary
                      : isCurrent
                      ? COMPANY_COLORS.secondary
                      : COMPANY_COLORS.surface,
                    borderColor: isCompleted
                      ? COMPANY_COLORS.primary
                      : isCurrent
                      ? COMPANY_COLORS.secondary
                      : COMPANY_COLORS.border,
                    color: isCompleted || isCurrent ? "#fff" : COMPANY_COLORS.textMuted,
                    boxShadow: isCurrent ? `0 0 0 3px ${COMPANY_COLORS.secondary}35` : "none",
                  }}
                >
                  {isCompleted ? "✓" : isCurrent ? step.icon : stepFlatIdx + 1}
                </div>

                {/* Label */}
                <span
                  className="mt-1.5 text-center leading-tight px-1"
                  style={{
                    fontSize: 9,
                    fontWeight: isCurrent ? 700 : isCompleted ? 600 : 400,
                    color: isCompleted
                      ? COMPANY_COLORS.primary
                      : isCurrent
                      ? COMPANY_COLORS.secondary
                      : COMPANY_COLORS.textMuted,
                    maxWidth: 72,
                  }}
                >
                  {step.key}
                </span>

                {/* Note annotation */}
                {step.note && isCurrent && (
                  <span
                    className="mt-0.5 text-center leading-tight px-1"
                    style={{
                      fontSize: 8,
                      color: COMPANY_COLORS.textMuted,
                      maxWidth: 72,
                      fontStyle: "italic",
                    }}
                  >
                    {step.note}
                  </span>
                )}

                {/* Current badge */}
                {isCurrent && (
                  <span
                    className="mt-1 px-1.5 py-0.5 rounded-full text-white font-semibold"
                    style={{ fontSize: 8, background: COMPANY_COLORS.secondary }}
                  >
                    Current
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Rejection banner */}
      {isRejected && (
        <div
          className="mt-3 rounded-lg px-3 py-2 flex items-center gap-2 text-xs"
          style={{ background: "#FEF2F2", border: "1px solid #FECACA", color: "#B91C1C" }}
        >
          <span style={{ fontSize: 14 }}>⚠️</span>
          <span>
            <strong>Off track:</strong> This loan is currently in a <strong>{status}</strong> state.
            It may be revived and return to the normal flow.
          </span>
        </div>
      )}
    </div>
  );
};

// ── ExpandedLoanDetails ───────────────────────────────────────────────────────

interface ExpandedLoanDetailsProps {
  loan: Loan;
  currency: string;
}

const ExpandedLoanDetails: React.FC<ExpandedLoanDetailsProps> = ({ loan, currency }) => {
  const detailSections = [
    {
      title: "Vehicle Information",
      accent: COMPANY_COLORS.primary,
      rows: [
        { label: "Make & Model", value: loan.vehicleInformation?.makeAndModel ?? loan.clientFirstName },
        { label: "Year",         value: loan.vehicleInformation?.year?.toString() ?? "2020" },
        { label: "VIN",          value: loan.vehicleInformation?.vin ?? "XXXX-XXXX-XXXX", mono: true },
        { label: "Mileage",      value: loan.vehicleInformation?.mileageKm ? `${loan.vehicleInformation.mileageKm.toLocaleString()} km` : "1,000 km" },
      ],
    },
    {
      title: "Offer Details",
      accent: "#1E88E5",
      rows: [
        { label: "Approved Amount", value: `${loan.offerDetails?.currency ?? currency} ${Number(loan.offerDetails?.approvedAmount ?? 816439).toLocaleString()}` },
        { label: "Interest Rate",   value: loan.offerDetails?.interestRate ? `${loan.offerDetails.interestRate}%` : "39.25%" },
        { label: "Tenure",          value: loan.offerDetails?.tenureMonths ? `${loan.offerDetails.tenureMonths} months` : "36 months" },
        { label: "Monthly Payment", value: `${loan.offerDetails?.currency ?? currency} ${Number(loan.offerDetails?.monthlyPayment ?? 85137).toLocaleString()}`, highlight: "#1565C0" },
        { label: "Equity",          value: `${loan.offerDetails?.currency ?? currency} ${Number(loan.offerDetails?.equity ?? 213827).toLocaleString()}` },
      ],
    },
    {
      title: "Additional Fees",
      accent: COMPANY_COLORS.secondary,
      rows: [
        { label: "Annual Maintenance",   value: `${loan.additionalFees?.currency ?? currency} ${Number(loan.additionalFees?.annualMaintenance ?? 430720).toLocaleString()}` },
        { label: "Vehicle Registration", value: `${loan.additionalFees?.currency ?? currency} ${Number(loan.additionalFees?.vehicleRegistration ?? 142500).toLocaleString()}` },
        { label: "Service Charge",       value: `${loan.additionalFees?.currency ?? currency} ${Number(loan.additionalFees?.serviceCharge ?? 37761).toLocaleString()}` },
      ],
      total: {
        label: "Total Upfront",
        value: `${loan.additionalFees?.currency ?? currency} ${Number(loan.additionalFees?.totalUpfront ?? 753703).toLocaleString()}`,
      },
    },
  ];

  return (
    <div className="pl-6 space-y-4">
      {/* ── Transaction Journey — now config-driven ── */}
      <TransactionJourney system={loan.system} status={loan.status} tasks={loan.task_statuses} />

      {/* ── Transaction Details ── */}
      <div
        className="rounded-xl p-4"
        style={{ background: COMPANY_COLORS.surface, border: `1px solid ${COMPANY_COLORS.border}` }}
      >
        <div
          className="mb-4 pb-2"
          style={{ borderBottom: `2px solid ${COMPANY_COLORS.secondary}` }}
        >
          <h3 className="text-xs font-bold uppercase tracking-wider" style={{ color: COMPANY_COLORS.primary }}>
            Transaction Details
          </h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {detailSections.map((section) => (
            <div
              key={section.title}
              className="rounded-xl p-4"
              style={{ background: COMPANY_COLORS.pageBg, border: `1px solid ${COMPANY_COLORS.border}` }}
            >
              <div className="mb-3 pb-2" style={{ borderBottom: `2px solid ${section.accent}` }}>
                <h4 className="text-xs font-bold uppercase tracking-wider" style={{ color: section.accent }}>
                  {section.title}
                </h4>
              </div>

              <div className="space-y-0">
                {section.rows.map((row, i) => (
                  <div
                    key={row.label}
                    className="flex justify-between items-center py-1.5 text-xs"
                    style={{
                      borderBottom: i < section.rows.length - 1 ? `1px solid ${COMPANY_COLORS.border}` : "none",
                    }}
                  >
                    <span style={{ color: COMPANY_COLORS.textMuted }}>{row.label}</span>
                    <span
                      className="font-semibold"
                      style={{
                        color: (row as any).highlight || COMPANY_COLORS.textPrimary,
                        fontFamily: (row as any).mono ? "monospace" : undefined,
                        fontSize: (row as any).mono ? 11 : undefined,
                      }}
                    >
                      {row.value}
                    </span>
                  </div>
                ))}

                {section.total && (
                  <div
                    className="flex justify-between items-center pt-2 mt-1 text-xs font-bold"
                    style={{ borderTop: `2px solid ${COMPANY_COLORS.border}`, color: COMPANY_COLORS.primary }}
                  >
                    <span>{section.total.label}</span>
                    <span style={{ color: "#1565C0", fontSize: 13 }}>{section.total.value}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
